import { injectable, inject } from "inversify";
import { ISessionService, ISessionServiceId } from "./services/interfaces/sessionService";
import { IMessagingService, IMessagingServiceId } from "./services/interfaces/messagingService";
import { ICookieService, ICookieServiceId } from "./services/interfaces/cookieService";
import { IEnquiryFormService, IEnquiryFormServiceId } from "./services/interfaces/enquiryFormService";
import { ComponentPage } from "./pages/componentPage";
import { AccordionComponent } from "./components/accordionComponent";
import { SiteLeftMenuComponent } from "./components/siteLeftMenuComponent";
import { ModalComponent } from "./components/modalComponent";
import { CookieModalComponent } from "./components/cookieModalComponent";
import { TitleComponent } from "./components/titleComponent";
import { TableComponent } from "./components/tableComponent";
import { TextComponent } from "./components/textComponent";
import { EnquiryFormComponent } from "./components/enquiryFormComponent";
import { NewsLetterFormComponent } from "./components/newsLetterFormComponent";
import { VideoComponent } from "./components/videoComponent";
import { CardsGridClickableComponent } from "./components/cardsGridClickableComponent";
import { CardsContainer } from "./components/cardsContainer";
import { CardStatsComponent } from "./components/cardStatsComponent";
import { HeaderComponent } from "./components/headerComponent";
import { MapComponent } from "./components/mapComponent";
import { NotificationComponent } from "./components/notificationComponent";
import { IApiClient, IApiClientId, IPlatformClient, IPlatformClientId } from "@se/platform";
import { ListingsPage } from "./components/listingsPage";
import { SearchAutoComplete } from "./components/searchAutoComplete";
import { SearchForm } from "./components/searchForm";
import { SearchResults } from "./components/searchResults";
import { SearchPager } from "./components/searchPager";
import { LoadingIndicator } from "./components/loadingIndicator";
import { IAddressLookupService, IAddressLookupServiceId } from "./services/interfaces/addressLookupService";
import { ILoggingServiceId, ILoggingService } from "./services/interfaces/loggingService";
import { IAnalyticsService, IAnalyticsServiceId } from "./services/interfaces/analyticsService";
import { LiveChatInteractionEvent } from "./models/analytics/liveChatInteractionEvent";
import { AnalyticsEventName } from "./enums/analyticsEventName";
import { LiveChatContainer } from "./components/liveChatContainer"
import { CustomDropdownComponent } from "./components/customDropdownComponent";
import { ContactUsComponent } from "./components/contactUsComponent";
import { IHashingService, IHashingServiceId } from "./services/interfaces/hashingService";
import { AnchorListComponent } from "./components/anchorListComponent";
// The startup class in DS Core is essentially the entry point into the site.
@injectable()
class Startup {

    constructor(
        @inject(IMessagingServiceId) protected messagingService: IMessagingService,
        @inject(ISessionServiceId) protected sessionService: ISessionService,
        @inject(IEnquiryFormServiceId) protected enquiryFormService: IEnquiryFormService,
        @inject(IAddressLookupServiceId) protected addressLookupService: IAddressLookupService,
        @inject(IPlatformClientId) protected platformClient: IPlatformClient,
        @inject(IApiClientId) protected apiClient: IApiClient,
        @inject(ICookieServiceId) protected cookieService: ICookieService,
        @inject(ILoggingServiceId) protected appInsights: ILoggingService,
        @inject(IAnalyticsServiceId) protected analyticsTrackingService: IAnalyticsService,
        @inject(IHashingServiceId) protected hashingService: IHashingService
    ) {
    }

    public start() {
        this.appInsights.InitialiseAppInsights();

        document.querySelectorAll(".js-page").forEach((element: HTMLElement) => {
            new ComponentPage(element, this.messagingService, this.sessionService);
        });

        document.querySelectorAll(".js-loading-overlay").forEach((element: HTMLElement) => {
            new LoadingIndicator(element, this.messagingService);
        });

        document.querySelectorAll(".js-header").forEach((element: HTMLElement) => {
            new HeaderComponent(element, this.messagingService);
        });

        document.querySelectorAll(".js-siteleftmenu").forEach((element: HTMLElement) => {
            new SiteLeftMenuComponent(element, this.messagingService, this.analyticsTrackingService);
        });

        document.querySelectorAll(".js-card-clickable[data-url]").forEach((element: HTMLElement) => {
            new CardsGridClickableComponent(element, this.messagingService);
        });

        document.querySelectorAll(".js-accordion-component").forEach((element: HTMLElement) => {
            new AccordionComponent(element, this.messagingService, this.analyticsTrackingService);
        });

        document.querySelectorAll(".js-cards-container").forEach((element: HTMLElement) => {
            new CardsContainer(element, this.messagingService, this.analyticsTrackingService);
        });

        document.querySelectorAll(".js-checkboxDropdown-component").forEach((element: HTMLElement) => {
            new CustomDropdownComponent(element, this.messagingService);
        });

        document.querySelectorAll(".js-cards-stats").forEach((element: HTMLElement) => {
            new CardStatsComponent(element, this.messagingService);
        });

        document.querySelectorAll(".js-search-results").forEach((element: HTMLFormElement) => {
            new SearchResults(element, this.messagingService);
        });

        document.querySelectorAll(".js-search-pagintation").forEach((element: HTMLFormElement) => {
            new SearchPager(element, this.messagingService, this.analyticsTrackingService);
        });

        document.querySelectorAll(".js-listingpage-pagination").forEach((element: HTMLFormElement) => {
            new ListingsPage(element, this.messagingService, this.analyticsTrackingService);
        });

        document.querySelectorAll(".js-anchorList-component").forEach((element: HTMLFormElement) => {
            new AnchorListComponent(element, this.messagingService, this.analyticsTrackingService);
        });



        if (document.querySelectorAll(".js-cookiemodal-component").length > 0) {

            //create the default cookie modal that new visitors see, if it doesn't need to show then it won't
            var cookieModal: CookieModalComponent = new CookieModalComponent(document.querySelector(".js-cookiemodal-component"), this.messagingService, this.cookieService, this.appInsights);
            cookieModal.showModal();

            //wireup every show cookie preferences button or link to show the cookie modal
            document.querySelectorAll(".js-cookiemodal-showdialog").forEach((element: HTMLElement) => {
                element.onclick = () => {
                    var cookieModal = new CookieModalComponent(document.querySelector(".js-cookiemodal-component"), this.messagingService, this.cookieService, this.appInsights);
                    cookieModal.ShowModalInEditMode();
                    return false;
                }
            })
        };

        document.querySelectorAll(".js-modal-component").forEach((element: HTMLElement) => {
            new ModalComponent(element, this.messagingService);
        });

        document.querySelectorAll(".js-title-component").forEach((element: HTMLElement) => {
            new TitleComponent(element, this.messagingService);
        });

        document.querySelectorAll("table").forEach((element: HTMLElement) => {
            new TableComponent(element, this.messagingService);
        });

        document.querySelectorAll(".js-text-component").forEach((element: HTMLElement) => {
            new TextComponent(element, this.messagingService);
        });
        document.querySelectorAll(".js-enquiryForm-component").forEach((element: HTMLElement) => {
            new EnquiryFormComponent(element, this.messagingService, this.sessionService, this.enquiryFormService, this.hashingService, this.addressLookupService, this.appInsights, this.analyticsTrackingService);
        });

        document.querySelectorAll(".js-notification-component").forEach((element: HTMLElement) => {
            new NotificationComponent(element, this.messagingService);
        });

        document.querySelectorAll(".js-livechat-container").forEach((element: HTMLElement) => {
            new LiveChatContainer(element);
        })

        document.querySelectorAll(".js-search-form").forEach((element: HTMLFormElement) => {
            if (element.dataset.autocomplete === "true") {
                new SearchAutoComplete(element, this.messagingService, this.appInsights);
            } else {
                new SearchForm(element, this.messagingService);
            }
        });

        document.querySelectorAll(".js-contactUs-component").forEach((element: HTMLElement) => {
            new ContactUsComponent(element, this.messagingService);
        });

        document.querySelectorAll(".js-newsLetter-component").forEach((element: HTMLElement) => {
            new NewsLetterFormComponent(element, this.apiClient, this.addressLookupService, this.appInsights, this.sessionService, this.messagingService, this.analyticsTrackingService, this.hashingService);
        });

        document.querySelectorAll(".js-video-component").forEach((element: HTMLElement) => {
            new VideoComponent(element, this.messagingService);
        });

        const maps = document.querySelectorAll(".js-map-component") as NodeListOf<HTMLElement>;
        if (maps.length > 0) {
            const APIkey = maps[0].dataset.key; // Assumes all maps can use the same key
            if (!APIkey) {
                this.appInsights.LogMessage("Tried to load Google Map without key");
                return;
            }
            let google: any;
            if (typeof (google) == 'undefined') {
                var tag = document.createElement('script');
                tag.src = `https://maps.googleapis.com/maps/api/js?key=${APIkey}&callback=initMap&v=weekly`;
                var firstScriptTag = document.getElementsByTagName('script')[0];
                firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

                window.initMap = function () {
                    Startup.prototype.wireUpAllMaps(maps);
                };
            }
            else {
                Startup.prototype.wireUpAllMaps(maps);
            }
        }

        if ('fonts' in document) {
            // Add utility classes for fonts loaded
            document.fonts.ready.then((fontFaces) => {
                const fonts = [...fontFaces];
                document.body.classList.add('fonts-loaded');
                var uniqueFontFamilies = Array.from(new Set(fonts.map((font: any) => font.family.toLowerCase().replace(" ", "-"))));
                uniqueFontFamilies.forEach(fontFamily => document.body.classList.add('fonts-loaded--' + fontFamily))
            });
        }

        window.onLiveChatConversation = function (eventData, eventInfo) {
            let livechatevent = new LiveChatInteractionEvent(eventData.engagementName); //name of the engagement as defined in Live Assist
            Startup.prototype.recordLiveChatEvent(AnalyticsEventName.LiveChatInteraction, livechatevent);
        };

        global.analyticsTrackingServiceforWindow = this.analyticsTrackingService;

        this.appInsights.StartPageViewTracking();
    }

    public wireUpAllMaps(maps) {
        maps.forEach((element: HTMLElement) => {
            new MapComponent(element, this.messagingService);
        });
    }

    public recordLiveChatEvent(analyticsEventName, liveChatEvent) {
        analyticsTrackingServiceforWindow.RecordEvent(analyticsEventName, liveChatEvent);
    }

};

export { Startup };

declare global {
    var gtag: Gtag.Gtag;
    var analyticsTrackingServiceforWindow: IAnalyticsService;

    interface Window {
        initMap: () => void;
        onLiveChatConversation: (eventData, eventInfo) => void;
    }
}